import * as Sentry from '@sentry/react';
import { isChunkLoadError } from '@/utils/chunkErrorHandler/chunkErrorHandler';

// Initialize Sentry
export const initSentry = () => {
  // Only initialize Sentry in production
  if (import.meta.env.MODE !== 'production') {
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Don't mask text — we want readable replays for UX debugging.
        maskAllText: false,
        // But always mask form inputs by default (passwords, OTPs, payment
        // fields). Anything you specifically want recorded should be opted-in
        // with `data-sentry-unmask` (covered by the `unblock` selector below).
        maskAllInputs: true,
        block: [
          // Selectors whose contents are NEVER recorded — cards, private keys.
          '[data-sentry-block]',
          '.private-key',
          '[data-private]',
        ],
        unblock: ['[data-sentry-unmask]'],
        // Network capture — adds request/response bodies for fetch/XHR to
        // replay timelines so you can see WHAT the user requested.
        networkDetailAllowUrls: [
          /^\/api\//,
          /espotz\.live\/api/,
          /lambda-api\.espotz\.live/,
        ],
        networkRequestHeaders: ['x-request-id', 'x-correlation-id'],
        networkResponseHeaders: ['x-request-id', 'x-correlation-id'],
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    // Error sampling
    sampleRate: 1.0,
    // Distributed tracing — propagate `sentry-trace` and `baggage` headers
    // to these origins so backend Sentry events can be linked to the FE
    // event that triggered them.
    //
    // NOTE: `api.espotz.live` is intentionally omitted while the AWS API
    // Gateway OPTIONS allow-headers list is being updated to include
    // `sentry-trace,baggage` (see espotz-iac/modules/api_gateway/main.tf).
    // Adding those headers caused the browser preflight to fail because
    // API Gateway's MOCK OPTIONS integration doesn't echo them back. Once
    // the IaC change is applied to prod, restore the broader regex
    // `/^https:\/\/(.+\.)?espotz\.live\//` here to re-enable FE↔BE trace
    // linkage end-to-end.
    tracePropagationTargets: ['localhost', /^\/api\//],
    // Attach a stack trace to every captured message (not just errors). Lets
    // you trace the call site of `Sentry.captureMessage(...)` calls.
    attachStacktrace: true,
    // Release tracking — value is injected at build time by vite.config.ts
    // (see `define: { 'import.meta.env.VITE_APP_VERSION': ... }`) so it
    // matches the release the @sentry/vite-plugin uploads source maps under.
    release: import.meta.env.VITE_APP_VERSION,
    sendDefaultPii: true,
    // Release health (sessions) is handled by the browser SDK by default;
    // `autoSessionTracking` was removed from Sentry.init options in SDK v8+.
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Suppress framework / browser / third-party noise that drowns the feed.
    ignoreErrors: [
      // Benign ResizeObserver loop warning fired by some browsers
      /ResizeObserver loop limit exceeded/,
      /ResizeObserver loop completed with undelivered notifications/,
      // Sequence WaaS sometimes throws this when the tab is backgrounded
      /^Load failed$/,
      // Browser extensions / wallets
      /chrome-extension:\/\//,
      /moz-extension:\/\//,
      // Google / FB pixel noise
      /Non-Error promise rejection captured/,
    ],
    // Drop events whose top frame originates from a third-party URL.
    denyUrls: [
      /\/assets\/wallet-vendor-/,
      /sequence\.app/,
      /waas\.sequence\.app/,
      /googletagmanager\.com/,
      /google-analytics\.com/,
      /\.posthog\.com/,
    ],

    // Additional options
    beforeSend(event, hint) {
      // Filter out non-critical errors in development
      if (import.meta.env.MODE === 'development') {
        // Only send critical errors in development
        if (event.level === 'error' || event.level === 'fatal') {
          return event;
        }
        return null;
      }

      // Drop chunk-load errors — they're already handled by the auto-reload
      // fallback in SentryErrorBoundary. Reuses the same detector so the
      // filter and the UI behavior never drift apart.
      const originalError = hint?.originalException;
      if (originalError && isChunkLoadError(originalError)) {
        return null;
      }

      // Filter out expected HTTP errors that shouldn't be reported
      // 401 = Unauthorized (session expired, not logged in - expected behavior)
      // 403 = Forbidden (user doesn't have permission - expected behavior)
      // 404 = Not Found (resource doesn't exist - expected behavior)
      if (event.contexts?.response?.status_code) {
        const statusCode = event.contexts.response.status_code;
        if ([401, 403, 404].includes(statusCode)) {
          return null; // Don't send to Sentry
        }
      }

      // Also check in tags or extra data for status codes
      if (event.tags?.status || event.extra?.status) {
        const statusCode = event.tags?.status || event.extra?.status;
        if ([401, 403, 404].includes(Number(statusCode))) {
          return null; // Don't send to Sentry
        }
      }

      // Check in contexts for API errors with status codes
      if (event.contexts?.api_error?.status) {
        const statusCode = event.contexts.api_error.status;
        if ([401, 403, 404].includes(Number(statusCode))) {
          return null; // Don't send to Sentry
        }
      }

      return event;
    },
    // Set user context
    beforeBreadcrumb(breadcrumb) {
      // Filter out sensitive data from breadcrumbs
      if (breadcrumb.category === 'http' && breadcrumb.data) {
        // Remove sensitive headers
        if (breadcrumb.data.headers) {
          delete breadcrumb.data.headers['authorization'];
          delete breadcrumb.data.headers['cookie'];
        }
      }
      return breadcrumb;
    },
  });
};

// Set user context
export const setSentryUser = (user: {
  id?: string;
  email?: string;
  username?: string;
}) => {
  if (import.meta.env.MODE !== 'production') return;

  Sentry.setUser({
    id: user?.id,
    email: user?.email,
    username: user?.username,
    // Add any other user properties you want to track
  });
};

// Clear user context
export const clearSentryUser = () => {
  if (import.meta.env.MODE !== 'production') return;
  Sentry.setUser(null);
};

// Add custom tags
export const setSentryTag = (key: string, value: string) => {
  if (import.meta.env.MODE !== 'production') return;
  Sentry.setTag(key, value);
};

// Add custom context
export const setSentryContext = (
  key: string,
  context: Record<string, unknown>,
) => {
  if (import.meta.env.MODE !== 'production') return;
  Sentry.setContext(key, context);
};

// Capture custom events
export const captureSentryEvent = (
  message: string,
  level: 'info' | 'warning' | 'error' | 'fatal' = 'info',
  extra?: Record<string, unknown>,
) => {
  if (import.meta.env.MODE !== 'production') return;
  Sentry.captureMessage(message, level);
  if (extra) {
    Sentry.setContext('custom', extra);
  }
};

// Capture exceptions
export const captureSentryException = (
  error: Error,
  extra?: Record<string, unknown>,
) => {
  if (import.meta.env.MODE !== 'production') return;
  Sentry.captureException(error);
  if (extra) {
    Sentry.setContext('error_context', extra);
  }
};

/**
 * Derive a feature area from the current URL pathname. Called on every route
 * change so all subsequent Sentry events are tagged `feature_area`. This makes
 * the issue feed filterable by area without any per-component instrumentation.
 *
 * Also extracts entity IDs from common URL shapes — `/tournament/:id`,
 * `/clan/:id`, `/org/:slug`, `/team/:id` — and tags them so any error on a
 * detail page is filterable by the entity that page is about.
 */
export const tagRouteContext = (pathname: string) => {
  if (import.meta.env.MODE !== 'production') return;

  // Clear stale entity tags from the previous route. Without this, navigating
  // from /tournament/abc to /clan/xyz would leave the old tournament_id tag.
  Sentry.setTag('tournament_id', undefined);
  Sentry.setTag('clan_id', undefined);
  Sentry.setTag('team_id', undefined);
  Sentry.setTag('org_slug', undefined);

  let area: string = 'other';

  if (pathname === '/' || pathname.startsWith('/home')) area = 'landing';
  else if (pathname.startsWith('/tournament')) {
    area = 'tournament';
    const m = pathname.match(/^\/tournament\/([^/]+)/);
    if (m?.[1]) Sentry.setTag('tournament_id', m[1]);
  } else if (pathname.startsWith('/tournaments')) area = 'tournaments_list';
  else if (pathname.startsWith('/play')) area = 'play';
  else if (pathname.startsWith('/clan/')) {
    area = 'clan';
    const m = pathname.match(/^\/clan\/([^/]+)/);
    if (m?.[1]) Sentry.setTag('clan_id', m[1]);
  } else if (pathname.startsWith('/clans')) area = 'clans_list';
  else if (pathname.startsWith('/team/')) {
    area = 'team';
    const m = pathname.match(/^\/team\/([^/]+)/);
    if (m?.[1]) Sentry.setTag('team_id', m[1]);
  } else if (pathname.startsWith('/org/')) {
    area = 'org_profile';
    const m = pathname.match(/^\/org\/([^/]+)/);
    if (m?.[1]) Sentry.setTag('org_slug', m[1]);
  } else if (pathname.startsWith('/organization/')) {
    area = 'org_admin';
  } else if (pathname.startsWith('/organizations')) area = 'orgs_list';
  else if (pathname.startsWith('/wallet')) area = 'wallet';
  else if (pathname.startsWith('/profile')) area = 'profile';
  else if (pathname.startsWith('/complete-profile')) area = 'complete_profile';
  else if (pathname.startsWith('/notifications')) area = 'notifications';
  else if (pathname.startsWith('/feedback')) area = 'feedback';
  else if (pathname.startsWith('/auth') || pathname.includes('login'))
    area = 'auth';

  Sentry.setTag('feature_area', area);
};
